import { z } from 'zod';
import { ReportingComparisonScopes } from '../enums';

const nullableNumberSchema = z.number().nullable();

export const reportingComparisonRangeSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
});

export type ReportingComparisonRangeDto = z.infer<typeof reportingComparisonRangeSchema>;

export const reportingComparisonSummarySchema = z.object({
  currentPremium: z.number(),
  currentPolicies: z.number(),
  comparisonPremium: z.number(),
  comparisonPolicies: z.number(),
  premiumDifference: z.number(),
  policyDifference: z.number(),
  premiumChangePercent: nullableNumberSchema,
  policyChangePercent: nullableNumberSchema,
  goalAmount: nullableNumberSchema,
  goalAttainedPercent: nullableNumberSchema,
  entitiesAheadOrOnTarget: z.number().int().nonnegative().nullable(),
});

export type ReportingComparisonSummaryDto = z.infer<typeof reportingComparisonSummarySchema>;

export const reportingComparisonRowSchema = z.object({
  entityName: z.string(),
  mgaName: z.string().nullable().optional(),
  goalAmount: nullableNumberSchema,
  goalAttainedPercent: nullableNumberSchema,
  currentPremium: z.number(),
  currentPolicies: z.number(),
  comparisonPremium: z.number(),
  comparisonPolicies: z.number(),
  premiumDifference: z.number(),
  policyDifference: z.number(),
  premiumChangePercent: nullableNumberSchema,
  policyChangePercent: nullableNumberSchema,
});

export type ReportingComparisonRowDto = z.infer<typeof reportingComparisonRowSchema>;

export const reportingComparisonTrendPointSchema = z.object({
  label: z.string(),
  currentPremium: z.number(),
  comparisonPremium: z.number(),
  currentPolicies: z.number(),
  comparisonPolicies: z.number(),
});

export type ReportingComparisonTrendPointDto = z.infer<typeof reportingComparisonTrendPointSchema>;

export const reportingComparisonRankingRowSchema = reportingComparisonRowSchema.extend({
  changeCategory: z.enum(['increase', 'decrease']),
});

export type ReportingComparisonRankingRowDto = z.infer<typeof reportingComparisonRankingRowSchema>;

export const reportingComparisonDatasetSchema = z.object({
  canDisplayGrid: z.boolean(),
  summary: reportingComparisonSummarySchema,
  overallRow: reportingComparisonRowSchema,
  overallRows: z.array(reportingComparisonRowSchema).optional(),
  topIncreases: z.array(reportingComparisonRowSchema),
  topDecreases: z.array(reportingComparisonRowSchema),
  topAgentIncreases: z.array(reportingComparisonRowSchema).optional(),
  topAgentDecreases: z.array(reportingComparisonRowSchema).optional(),
  allMgaRows: z.array(reportingComparisonRowSchema).optional(),
  allAgentRows: z.array(reportingComparisonRowSchema).optional(),
  trend: z.array(reportingComparisonTrendPointSchema),
  ranking: z.array(reportingComparisonRankingRowSchema),
});

export type ReportingComparisonDatasetDto = z.infer<typeof reportingComparisonDatasetSchema>;

export const reportingComparisonResponseSchema = z.object({
  scope: z.nativeEnum(ReportingComparisonScopes),
  groupByLabel: z.string(),
  currentRange: reportingComparisonRangeSchema,
  comparisonRange: reportingComparisonRangeSchema,
  paid: reportingComparisonDatasetSchema,
  submit: reportingComparisonDatasetSchema,
});

export type ReportingComparisonResponseDto = z.infer<typeof reportingComparisonResponseSchema>;

/**
 * One comparison table's page. `data` / `totalCount` rather than a shape of its own: that is
 * what `GridLoadResponse` uses elsewhere and what a DevExtreme CustomStore reads directly.
 *
 * `totalCount` is the whole ordered set, not this page — a table needs it to say "25 of 130"
 * and to know whether anything is left to load.
 */
export const reportingComparisonRowsResponseSchema = z.object({
  data: z.array(reportingComparisonRowSchema),
  totalCount: z.number().int().nonnegative(),
});

export type ReportingComparisonRowsResponseDto = z.infer<typeof reportingComparisonRowsResponseSchema>;
